import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { userAtom } from '@/atoms/user.atom';
import { useDialog } from '@/providers/DialogProvider';
import { Key, Shield } from '@mynaui/icons-react';
import { useAtomValue } from 'jotai';
import { FunctionComponent } from 'react';
import { ChangePasswordDialog } from './dialogs/ChangePasswordDialog';
import { Setup2FADialog } from './dialogs/Setup2FADialog';

interface iLabelProps {
  title: string;
  description: string;
}
const Label: FunctionComponent<iLabelProps> = ({ title, description }) => {
  return (
    <div className='flex flex-col gap-1'>
      <h3 className='font-medium leading-none text-slate-900 dark:text-white'>{title}</h3>
      <p className='text-sm font-medium leading-none text-slate-500 dark:text-white/70'>{description}</p>
    </div>
  );
};
interface SecuritySettingsProps {
  userId: string;
}

export const SecuritySettings: FunctionComponent<SecuritySettingsProps> = ({ userId }) => {
  const { openDialog } = useDialog();
  const me = useAtomValue(userAtom);
  const twoFactorEnabled = (me as any)?.twoFactorEnabled;
  const twoFactorType = (me as any)?.twoFactorType as 'SMS' | 'AUTHENTICATOR' | null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Security Settings</CardTitle>
        <CardDescription>Manage your password and two-factor authentication.</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Password Section */}
        <div className='space-y-4'>
          <div className='flex items-center justify-between'>
            <Label title='Password' description='Change your account password' />
            <Button variant='outline' onClick={() => openDialog(<ChangePasswordDialog userId={userId} />)}>
              <Key className='h-4 w-4 mr-2 stroke-2' />
              Change Password
            </Button>
          </div>
        </div>

        {/* Two-Factor Authentication */}
        <div className='space-y-4'>
          <div className='flex items-center justify-between'>
            <Label title='Two-Factor Authentication' description='Add an extra layer of security to your account' />
            <div className='flex items-center space-x-2'>
              <Badge variant={twoFactorEnabled ? 'default' : 'secondary'}>
                {twoFactorEnabled ? 'Enabled' : 'Not Enabled'}
              </Badge>
              <Button
                variant='outline'
                onClick={() =>
                  openDialog(
                    <Setup2FADialog
                      mobileNumber={(me as any)?.mobileNumber}
                      twoFactorEnabled={twoFactorEnabled}
                      twoFactorType={twoFactorType}
                    />,
                  )
                }>
                <Shield className='h-4 w-4 mr-2 stroke-2' />
                {twoFactorEnabled ? 'Manage 2FA' : 'Enable 2FA'}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
